import { get, post, put, del } from '@/lib/api/client'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Document,
  CreateDocument,
  UpdateDocument,
  UpdateDocumentStatus,
  DocumentFilterParams,
  DocumentStats
} from '@/lib/api/types'

export const documentService = {
  // Filter documents with pagination
  filterDocuments: async (
    params: PaginationParams = { page: 1, size: 10 },
    filters: DocumentFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<Document>>> => {
    return await post<ApiResponse<PaginatedResponse<Document>>>(
      `documents/private/filter?size=${params.size}&page=${params.page}`,
      filters
    )
  },

  // Get document by ID
  getDocumentById: async (documentId: string): Promise<ApiResponse<Document>> => {
    return await get<ApiResponse<Document>>(`documents/private/${documentId}`)
  },

  // Create a new document
  createDocument: async (data: CreateDocument): Promise<ApiResponse<Document>> => {
    return await post<ApiResponse<Document>>(
      'documents/admin/private',
      data
    )
  },

  // Update an existing document
  updateDocument: async (
    documentId: string,
    data: UpdateDocument
  ): Promise<ApiResponse<Document>> => {
    return await put<ApiResponse<Document>>(
      `documents/admin/private/${documentId}`,
      data
    )
  },

  // Update document status
  updateDocumentStatus: async (
    documentId: string,
    data: UpdateDocumentStatus
  ): Promise<ApiResponse<null>> => {
    return await put<ApiResponse<null>>(
      `documents/admin/private/${documentId}/status`,
      data
    )
  },

  // Delete a document
  deleteDocument: async (documentId: string): Promise<ApiResponse<null>> => {
    return await del<ApiResponse<null>>(
      `documents/admin/private/${documentId}`
    )
  },

  // Get draft documents count
  getDraftDocumentsCount: async (): Promise<ApiResponse<number>> => {
    return await get<ApiResponse<number>>('documents/private/stats/draft')
  },

  // Get total documents count
  getTotalDocumentsCount: async (): Promise<ApiResponse<number>> => {
    return await get<ApiResponse<number>>('documents/private/stats/total')
  },

  // Search documents
  searchDocuments: async (
    searchBy?: string
  ): Promise<ApiResponse<Document[]>> => {
    const queryParams = new URLSearchParams()
    
    if (searchBy !== undefined) {
      queryParams.append('searchBy', searchBy)
    }

    return await get<ApiResponse<Document[]>>(
      `documents/private/search?${queryParams.toString()}`
    )
  },

  // Get document categories
  getDocumentCategories: async (): Promise<ApiResponse<Array<{ value: string; displayName: string }>>> => {
    return await get<ApiResponse<Array<{ value: string; displayName: string }>>>(
      'documents/private/document-category'
    )
  },

  // Get document stats
  getDocumentStats: async (): Promise<ApiResponse<DocumentStats>> => {
    return await get<ApiResponse<DocumentStats>>(
      'documents/private/stats'
    )
  }
}
